import { Button, Flex, Stack, Text } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Content, NoContentPlaceholder, PendingSpinner } from '@/core/components';
import { Pagination } from '@/core/components/Pagination/Pagination';
import { usePagination } from '@/core/components/Pagination/usePagination';

import { useCurrentUser } from '@feature/security';
import { FilterBox, PagedResponse, SearchFilter, useSearch } from '@feature/utils';

import { LocalAccountCreate } from './LocalAccountCreate';
import { LocalAccountTable } from './components/LocalAccountTable';
import { getUserLocalPagedOptions } from './model/UserQuery';
import { LocalUserParams } from './model/UserTypes';
import { isLocalAccountAdminRole } from './userRoles';

const PAGE_SIZE = 20;

export function LocalAccountBrowse() {
  const { t } = useTranslation();
  const { currentRole } = useCurrentUser();

  if (!isLocalAccountAdminRole(currentRole)) {
    return <NoContentPlaceholder title={t('localAccounts.noAccess')} />;
  }

  return <LocalAccountList />;
}

function LocalAccountList() {
  const { t } = useTranslation();
  const { search, searchProps } = useSearch();
  const { pagination, useTotalElements, ...paginationProps } = usePagination({
    pageSize: PAGE_SIZE,
    search,
  });
  const [creating, setCreating] = useState(false);

  return (
    <Stack>
      <Flex
        justify="space-between"
        align="center"
      >
        <Text
          component="h1"
          size="lg"
          fw="500"
        >
          {t('localAccounts.title')}
        </Text>
        <Button
          size="xs"
          leftSection={<IconPlus size={14} />}
          onClick={() => setCreating(true)}
        >
          {t('localAccounts.add')}
        </Button>
      </Flex>

      <FilterBox>
        <SearchFilter
          label={t('label.search')}
          description={t('localAccounts.search')}
          {...searchProps}
        />
      </FilterBox>

      <Content type="full">
        <Suspense fallback={<PendingSpinner h="300px" />}>
          <LocalAccountListing
            params={{ page: pagination.page, size: pagination.pageSize, search }}
            useTotalElements={useTotalElements}
            paginationProps={paginationProps}
          />
        </Suspense>
      </Content>

      {creating && <LocalAccountCreate onClose={() => setCreating(false)} />}
    </Stack>
  );
}

interface LocalAccountListingProps {
  params: LocalUserParams;
  useTotalElements: (response: PagedResponse<unknown> | undefined | number) => void;
  paginationProps: React.ComponentProps<typeof Pagination>;
}

function LocalAccountListing({ params, useTotalElements, paginationProps }: LocalAccountListingProps) {
  const { t } = useTranslation();
  const { data } = useSuspenseQuery(getUserLocalPagedOptions(params));

  useTotalElements(data);

  if (data.list.length === 0) {
    return <NoContentPlaceholder title={t('localAccounts.empty')} />;
  }

  return (
    <>
      <LocalAccountTable users={data.list} />
      <Pagination {...paginationProps} />
    </>
  );
}
